/**
 * MALÁ page transitions for Shopify's traditional (multi-page) navigation.
 *
 * Adds `mala-loader--leaving` to <html> when a real same-origin page load starts (Navigation API,
 * or a same-origin link click where that API is missing); CSS shows
 * the curtain after a short delay so instant loads never flash. The arriving page's inline head
 * script decides whether to continue the curtain (see snippets/mala-page-loader.liquid).
 * Nothing here delays navigation: the browser navigates as usual while the curtain fades in.
 */

const root = document.documentElement;
const STORAGE_KEY = 'mala:page-loader';
const LEAVING = 'mala-loader--leaving';
const ENTERING = 'mala-loader--entering';

/** Paths that belong to the cart and checkout flow, or that respond with a download or redirect. */
const EXCLUDED_PATHS = [/^\/cart(\/|$)/, /^\/checkouts?(\/|$)/, /^\/account\/logout/, /^\/apps\//, /^\/services\//, /^\/tools\//];

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * @param {MouseEvent} event
 * @returns {HTMLAnchorElement | null} The link that will navigate the page, if this click should get a transition.
 */
function navigatingLink(event) {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
  if (!(event.target instanceof Element)) return null;

  const link = event.target.closest('a[href]');
  if (!(link instanceof HTMLAnchorElement)) return null;
  if (link.closest('[data-no-page-loader]') || link.hasAttribute('download')) return null;
  if (link.target && link.target !== '_self') return null;
  if (link.getAttribute('role') === 'button') return null;

  let url;
  try {
    url = new URL(link.href, window.location.href);
  } catch {
    return null;
  }

  if (!isTransitionTarget(url)) return null;

  // In-page anchors and links to the exact current URL don't load a new page.
  const samePage = url.pathname === window.location.pathname && url.search === window.location.search;
  if (samePage && (url.hash || link.getAttribute('href')?.startsWith('#'))) return null;

  return link;
}

function start() {
  root.classList.remove(ENTERING);
  root.classList.add(LEAVING);
  try {
    sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {}
}

function reset() {
  root.classList.remove(LEAVING, ENTERING);
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {}
}

/** @param {URL} url */
function isTransitionTarget(url) {
  if (url.origin !== window.location.origin || !url.protocol.startsWith('http')) return false;
  return !EXCLUDED_PATHS.some((pattern) => pattern.test(url.pathname));
}

const navigationApi = /** @type {any} */ (window).navigation;

if (navigationApi) {
  // The Navigation API reports every real page load, including ones the theme starts from script
  // after cancelling the click (e.g. product grids), and none of the clicks it handles in place.
  navigationApi.addEventListener('navigate', (/** @type {any} */ event) => {
    if (reducedMotion.matches) return;
    if (event.hashChange || event.downloadRequest !== null || event.formData) return;
    if (event.navigationType === 'reload' || event.navigationType === 'traverse') return;
    if (event.destination?.sameDocument) return;

    let url;
    try {
      url = new URL(event.destination.url);
    } catch {
      return;
    }
    if (isTransitionTarget(url)) start();
  });
  // A newer navigation aborts the one it replaces (the theme re-issues some link clicks from
  // script), so an AbortError is not a failure; real errors leave the page in place.
  navigationApi.addEventListener('navigateerror', (/** @type {any} */ event) => {
    if (event.error?.name !== 'AbortError') reset();
  });
} else {
  document.addEventListener('click', (event) => {
    if (reducedMotion.matches || !navigatingLink(event)) return;

    // Let every other handler (menus, carousels, quick add, filters, cart drawer…) run first; only a
    // click that nobody cancelled turns into a page load. The navigation itself is already under way.
    window.setTimeout(() => {
      if (!event.defaultPrevented) start();
    });
  });
}

// Restored from the back/forward cache, or returning to a tab: never show a stale curtain.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) reset();
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && root.classList.contains(LEAVING)) reset();
});

// A navigation that ends in a download or a 204 leaves the page in place: the document becomes
// visible/interactive again without unloading, so drop the curtain then.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && root.classList.contains(LEAVING)) {
    window.requestAnimationFrame(() => {
      if (document.visibilityState === 'visible') reset();
    });
  }
});
